// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { hrReviewKRAsUrl, submitHRReviewUrl, getAllDesignations, saveHRReviewDraftUrl } from "../../../Service/Apis";
import { getStatusChip, formatDate, getPerformanceGrade, KRAStatus, getPeriodShortLabel, getPeriodDaysWarningColor, formatRatingLabel } from "../utils";

interface KPIRating {
  kpi_id: string;
  question: string;
  weightage: number;
  rating_type?: 'numerical' | 'likert' | 'boolean';
  self_rating: number | null;
  manager_rating: number | null;
  self_comments?: string | null;
  manager_comments?: string | null;
}

interface KRASection {
  section_id: string;
  section: string;
  weightage: number;
  questions: KPIRating[];
}

interface EmployeeKRA {
  employee_kra_id: string;
  employee_name: string;
  employee_code: string;
  employee_email: string;
  form_name: string;
  designation: string;
  assigned_date: string;
  accepted_date: string | null;
  status: KRAStatus;
  sections: KRASection[];
  self_review_overall_rating: number | null;
  self_review_overall_comments: string | null;
  manager_overall_rating: number | null;
  manager_overall_comments: string | null;
  review_period: string;
  period_start_date: string;
  period_end_date: string;
  days_remaining: number;
  is_period_expired: boolean;
  manager_name?: string | null;
  // HR review fields (for draft loading)
  hr_overall_rating?: number | null;
  hr_overall_comments?: string | null;
  increment_percentage?: number | null;
  promotion_recommended?: boolean;
  promoted_to_designation_id?: string | null;
  final_grade?: string | null;
}

interface Designation {
  id: string;
  designation_name: string;
}

interface HRReviewFormData {
  hr_overall_rating: number;
  hr_overall_comments: string;
  increment_percentage: number | null;
  promotion_recommended: boolean;
  promoted_to_designation_id: string | null;
  final_grade: string;
}

const HRReview: React.FC = () => {
  // useState hooks
  const [employees, setEmployees] = useState<EmployeeKRA[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("awaiting_hr_review");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeKRA | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  const [submittingReview, setSubmittingReview] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [showCommentsError, setShowCommentsError] = useState<boolean>(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  const [hrReviewData, setHrReviewData] = useState<HRReviewFormData>({
    hr_overall_rating: 0,
    hr_overall_comments: "",
    increment_percentage: null,
    promotion_recommended: false,
    promoted_to_designation_id: null,
    final_grade: "C",
  });

  // const variables (hooks, Redux, computed constants)
  // Other constants and hooks
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> = useDispatch();

  // Fetch employees and designations data
  useEffect(() => {
    fetchEmployees();
    fetchDesignations();
  }, []);

  // Filter and sort employees
  useEffect(() => {
    filterAndSortEmployees();
  }, [searchTerm, statusFilter, employees, sortField, sortOrder]);

  // Function declarations
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchData(hrReviewKRAsUrl));
      
      if (response && Array.isArray(response)) {
        setEmployees(response);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load employee KRAs";
      showToast(errorMessage, "error", true);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await dispatch(fetchData(getAllDesignations));
      
      if (response && Array.isArray(response)) {
        setDesignations(response);
      }
    } catch (error: unknown) {
      // Don't show error toast, it's not critical
    }
  };

  const filterAndSortEmployees = () => {
    let filtered = employees;

    // Filter by status
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((emp) => emp.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (emp) =>
          emp.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.employee_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string | number | undefined;
        let bValue: string | number | undefined;
        
        switch (sortField) {
          case "employee_name":
            aValue = a.employee_name?.toLowerCase() || "";
            bValue = b.employee_name?.toLowerCase() || "";
            break;
          case "form_name":
            aValue = a.form_name?.toLowerCase() || "";
            bValue = b.form_name?.toLowerCase() || "";
            break;
          case "review_period":
            // Map period to sortable values
            const periodOrder: { [key: string]: number } = {
              'monthly': 1,
              'quarterly': 2,
              'half_yearly': 3,
              'yearly': 4
            };
            aValue = periodOrder[a.review_period] || 0;
            bValue = periodOrder[b.review_period] || 0;
            break;
          case "self_review_overall_rating":
            aValue = a.self_review_overall_rating ?? -1;
            bValue = b.self_review_overall_rating ?? -1;
            break;
          case "manager_overall_rating":
            aValue = a.manager_overall_rating ?? -1;
            bValue = b.manager_overall_rating ?? -1;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredEmployees(filtered);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleReviewClick = (employee: EmployeeKRA) => {
    // Deep copy to avoid state mutation
    setSelectedEmployee(JSON.parse(JSON.stringify(employee)));
    
    // Initialize HR review data with existing draft values or defaults
    setHrReviewData({
      hr_overall_rating: employee.hr_overall_rating || 0,
      hr_overall_comments: employee.hr_overall_comments || "",
      increment_percentage: employee.increment_percentage || null,
      promotion_recommended: employee.promotion_recommended || false,
      promoted_to_designation_id: employee.promoted_to_designation_id || null,
      final_grade: employee.final_grade || "C",
    });
    
    setReviewDialogOpen(true);
  };

  const calculateInitialGrade = (rating: number): string => {
    const percentage = (rating / 5) * 100;
    if (percentage >= 81) return "A";
    if (percentage >= 61) return "B";
    if (percentage >= 41) return "C";
    return "D";
  };

  const handleHRRatingChange = (value: number) => {
    const newGrade = calculateInitialGrade(value);
    setHrReviewData({
      ...hrReviewData,
      hr_overall_rating: value,
      final_grade: newGrade,
    });
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!selectedEmployee) return;

    if (savingDraft) return; // Prevent double save

    setSavingDraft(true);
    try {
      const payload = {
        hr_overall_rating: hrReviewData.hr_overall_rating,
        hr_overall_comments: hrReviewData.hr_overall_comments || "",
        increment_percentage: hrReviewData.increment_percentage || null,
        promotion_recommended: hrReviewData.promotion_recommended,
        promoted_to_designation_id: hrReviewData.promotion_recommended 
          ? hrReviewData.promoted_to_designation_id 
          : null,
        final_grade: hrReviewData.final_grade || "",
      };

      await axios.post(
        saveHRReviewDraftUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      showToast("Draft saved successfully! Refreshing data...", "success", true);
      
      // Refresh the data to show saved draft
      await fetchEmployees();
      
      // Close dialog to show updated data
      setReviewDialogOpen(false);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || "Failed to save draft";
      showToast(errorMessage, "error", true);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitHRReview = async () => {
    if (!selectedEmployee) return;

    // Prevent double submission
    if (submittingReview) return;

    // Validation - Rating of 0 is valid, but check if it's been set (not just default)
    // We allow 0 as a valid rating (0-5 scale)
    if (hrReviewData.hr_overall_rating < 0 || hrReviewData.hr_overall_rating > 5) {
      showToast("HR rating must be between 0 and 5", "warning", true);
      return;
    }

    if (!hrReviewData.final_grade) {
      showToast("Please select a final grade", "warning", true);
      return;
    }

    if (hrReviewData.promotion_recommended && !hrReviewData.promoted_to_designation_id) {
      showToast("Please select a designation for promotion", "warning", true);
      return;
    }

    // Validation: Check if HR comments are provided
    if (!hrReviewData.hr_overall_comments || hrReviewData.hr_overall_comments.trim() === "") {
      setShowCommentsError(true);
      showToast("Please provide HR comments before submitting the review.", "error", true);
      return;
    }
    setShowCommentsError(false);

    try {
      setSubmittingReview(true);

      // Clean up payload - ensure null values for optional fields
      const payload = {
        ...hrReviewData,
        increment_percentage: hrReviewData.increment_percentage || null,
        promoted_to_designation_id: hrReviewData.promotion_recommended 
          ? hrReviewData.promoted_to_designation_id 
          : null,
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        submitHRReviewUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showToast("HR review submitted successfully!", "success", true);
      setReviewDialogOpen(false);
      setSelectedEmployee(null);
      
      // Refresh the list
      fetchEmployees();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to submit HR review";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmittingReview(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchEmployees();
    fetchDesignations();
  }, []);

  useEffect(() => {
    filterAndSortEmployees();
  }, [searchTerm, statusFilter, employees, sortField, sortOrder]);

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 5 }}>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "600" }}>
                HR Performance Review
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review and finalize employee performance ratings
              </Typography>
            </Box>
            <Chip
              label={`${filteredEmployees.length} ${filteredEmployees.length === 1 ? "Employee" : "Employees"}`}
              color="primary"
              variant="outlined"
            />
          </Box>

          {/* Search and Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, email, or employee code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="awaiting_hr_review">Awaiting HR Review</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : filteredEmployees.length === 0 ? (
            /* Empty State */
            <Box sx={{ textAlign: "center", py: 8 }}>
              <PersonIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No employees found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "No employees are awaiting HR review at this time"}
              </Typography>
            </Box>
          ) : (
            /* Table */
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortField === "employee_name"}
                        direction={sortField === "employee_name" ? sortOrder : "asc"}
                        onClick={() => handleSort("employee_name")}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortField === "form_name"}
                        direction={sortField === "form_name" ? sortOrder : "asc"}
                        onClick={() => handleSort("form_name")}
                      >
                        KRA
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Assigned</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortField === "review_period"}
                        direction={sortField === "review_period" ? sortOrder : "asc"}
                        onClick={() => handleSort("review_period")}
                      >
                        Period
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortField === "self_review_overall_rating"}
                        direction={sortField === "self_review_overall_rating" ? sortOrder : "asc"}
                        onClick={() => handleSort("self_review_overall_rating")}
                      >
                        Self Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortField === "manager_overall_rating"}
                        direction={sortField === "manager_overall_rating" ? sortOrder : "asc"}
                        onClick={() => handleSort("manager_overall_rating")}
                      >
                        Manager Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.employee_kra_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "600" }}>
                            {employee.employee_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {employee.employee_code} • {employee.employee_email}
                          </Typography>
                          {employee.manager_name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              Manager: {employee.manager_name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{employee.form_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(employee.assigned_date)}
                        </Typography>
                        {employee.accepted_date && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Accepted: {formatDate(employee.accepted_date)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Chip 
                            label={getPeriodShortLabel(employee.review_period)} 
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            End: {formatDate(employee.period_end_date)}
                          </Typography>
                          {employee.days_remaining > 0 && !employee.is_period_expired && (
                            <Chip 
                              label={`${employee.days_remaining}d left`}
                              size="small"
                              color={getPeriodDaysWarningColor(employee.days_remaining)}
                            />
                          )}
                          {employee.is_period_expired && (
                            <Chip label="Expired" size="small" color="error" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(employee.status)}</TableCell>
                      
                      {/* Self Score */}
                      <TableCell>
                        {employee.self_review_overall_rating != null ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "600", color: "info.main" }}>
                              {Number(employee.self_review_overall_rating).toFixed(2)}/5.00
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not rated
                          </Typography>
                        )}
                      </TableCell>
                      
                      {/* Manager Score */}
                      <TableCell>
                        {employee.manager_overall_rating != null ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "600", color: "primary.main" }}>
                              {Number(employee.manager_overall_rating).toFixed(2)}/5.00
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not rated
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell align="right">
                        <Button
                          variant={employee.status === "awaiting_hr_review" ? "contained" : "outlined"}
                          size="small"
                          onClick={() => handleReviewClick(employee)}
                          disabled={employee.status !== "awaiting_hr_review" && employee.status !== "completed"}
                        >
                          {employee.status === "awaiting_hr_review" ? "Review" : employee.status === "completed" ? "View" : "Pending"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* HR Review Dialog */}
      <Dialog 
        open={reviewDialogOpen} 
        onClose={() => !submittingReview && !savingDraft && setReviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "600" }}>
              HR Final Review - {selectedEmployee?.employee_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedEmployee?.form_name} • {selectedEmployee?.designation}
            </Typography>
            {selectedEmployee?.manager_name && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                👔 Manager: {selectedEmployee.manager_name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedEmployee && (
            <Box>
              {/* Performance Summary */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 1 }}>
                  📊 Performance Summary
                </Typography>
                <Typography variant="body2">
                  Review the employee's self-assessment and manager's evaluation before providing your final HR rating.
                </Typography>
              </Alert>

              {/* Employee Overall Comments */}
              {selectedEmployee.self_review_overall_comments && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "2px solid #2196f3" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "info.main", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    💭 Employee's Overall Self-Review Comments
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
                    {selectedEmployee.self_review_overall_comments}
                  </Typography>
                </Box>
              )}

              {/* Manager Overall Comments */}
              {selectedEmployee.manager_overall_comments && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, border: "2px solid #4caf50" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "success.main", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    👔 Manager's Overall Feedback & Comments
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
                    {selectedEmployee.manager_overall_comments}
                  </Typography>
                </Box>
              )}

              {/* Employee and Manager Ratings Side by Side */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Employee Self-Rating */}
                {selectedEmployee.self_review_overall_rating != null && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Employee Self-Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                        {Number(selectedEmployee.self_review_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Manager Rating */}
                {selectedEmployee.manager_overall_rating != null && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Manager Rating & Feedback
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                        {Number(selectedEmployee.manager_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "success.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.manager_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Detailed KPI Breakdown Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: "600", mb: 2 }}>
                  📊 Detailed KPI Breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Review individual KPI ratings and comments from both employee and manager.
                </Typography>
                
                {selectedEmployee.sections.map((section, sectionIndex) => (
                  <Accordion key={sectionIndex} elevation={0} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "grey.50" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: 2 }}>
                        <Typography sx={{ fontWeight: "600" }}>
                          {sectionIndex + 1}. {section.section}
                        </Typography>
                        <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                          {section.weightage}%
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      {section.questions.map((kpi, kpiIndex) => (
                        <Box 
                          key={kpiIndex} 
                          sx={{ 
                            mb: 3, 
                            pb: 3, 
                            borderBottom: kpiIndex < section.questions.length - 1 ? "1px solid" : "none", 
                            borderColor: "grey.200" 
                          }}
                        >
                          {/* KPI Title */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "15px", flex: 1 }}>
                              {kpi.question}
                            </Typography>
                            <Chip label={`${kpi.weightage}%`} size="small" color="primary" sx={{ ml: 2 }} />
                          </Box>

                          {/* Side-by-side Employee & Manager */}
                          <Grid container spacing={2}>
                            {/* Employee Self-Assessment */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, height: "100%" }}>
                                <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main" }}>
                                  👤 Employee Self-Assessment
                                </Typography>
                                <Typography variant="h6" sx={{ color: "info.main", fontWeight: "700", my: 1 }}>
                                  {kpi.self_rating !== null && kpi.self_rating !== undefined ? `${formatRatingLabel(kpi.self_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : "Not Rated"}
                                </Typography>
                                {kpi.self_comments && (
                                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "info.lighter" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "500", color: "info.dark" }}>
                                      Employee Comments:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                      {kpi.self_comments}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>

                            {/* Manager Assessment */}
                            <Grid item xs={12} md={6}>
                              <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, height: "100%" }}>
                                <Typography variant="caption" sx={{ fontWeight: "600", color: "success.main" }}>
                                  👔 Manager Assessment
                                </Typography>
                                <Typography variant="h6" sx={{ color: "success.main", fontWeight: "700", my: 1 }}>
                                  {kpi.manager_rating !== null && kpi.manager_rating !== undefined ? `${formatRatingLabel(kpi.manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : "Not Rated"}
                                </Typography>
                                {kpi.manager_comments && (
                                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "success.lighter" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "500", color: "success.dark" }}>
                                      Manager Comments:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                      {kpi.manager_comments}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* HR Final Review Section */}
              <Box sx={{ p: 3, backgroundColor: "grey.100", borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "600", mb: 3 }}>
                  🎯 HR Final Review & Decisions
                </Typography>

                {/* HR Rating Slider */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 2 }}>
                    HR Final Rating *
                  </Typography>
                  <Box sx={{ px: 2, mb: 2 }}>
                    <Slider
                      value={hrReviewData.hr_overall_rating}
                      onChange={(_, value) => handleHRRatingChange(Array.isArray(value) ? value[0] : value)}
                      min={0}
                      max={5}
                      step={0.5}
                      marks={[
                        { value: 0, label: '0' },
                        { value: 1, label: '1' },
                        { value: 2, label: '2' },
                        { value: 3, label: '3' },
                        { value: 4, label: '4' },
                        { value: 5, label: '5' },
                      ]}
                      valueLabelDisplay="on"
                      sx={{
                        '& .MuiSlider-valueLabel': {
                          fontSize: 14,
                          fontWeight: 'bold',
                          backgroundColor: 'success.main',
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: "700", color: "grey.700", mb: 0.5 }}>
                      {Number(hrReviewData.hr_overall_rating).toFixed(2)}/5.00
                    </Typography>
                    <Typography variant="body2" sx={{ color: "grey.700", fontWeight: "600" }}>
                      {((Number(hrReviewData.hr_overall_rating) / 5) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>

                {/* Final Grade, Increment, Promotion */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Final Grade *</InputLabel>
                      <Select
                        value={hrReviewData.final_grade}
                        label="Final Grade *"
                        onChange={(e) => setHrReviewData({ ...hrReviewData, final_grade: e.target.value })}
                      >
                        <MenuItem value="A">A - Outstanding (81-100%)</MenuItem>
                        <MenuItem value="B">B - Exceeds Expectations (61-80%)</MenuItem>
                        <MenuItem value="C">C - Meets Expectations (41-60%)</MenuItem>
                        <MenuItem value="D">D - Needs Improvement (0-40%)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Increment Percentage */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Increment % (Optional)"
                      type="number"
                      value={hrReviewData.increment_percentage === null ? '' : hrReviewData.increment_percentage}
                      onChange={(e) => setHrReviewData({ ...hrReviewData, increment_percentage: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      InputProps={{
                        inputProps: { min: 0, max: 100, step: 0.5 }
                      }}
                      helperText="e.g., 10 for 10% increment (leave empty if not applicable)"
                    />
                  </Grid>

                  {/* Promotion Checkbox */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hrReviewData.promotion_recommended}
                            onChange={(e) => setHrReviewData({ ...hrReviewData, promotion_recommended: e.target.checked, promoted_to_designation_id: e.target.checked ? hrReviewData.promoted_to_designation_id : null })}
                          />
                        }
                        label="Recommend for Promotion"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Promoted To Designation - Shows only when promotion is recommended */}
                {hrReviewData.promotion_recommended && (
                  <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Promote to Designation *</InputLabel>
                      <Select
                        value={hrReviewData.promoted_to_designation_id || ""}
                        label="Promote to Designation *"
                        onChange={(e) => setHrReviewData({ ...hrReviewData, promoted_to_designation_id: e.target.value || null })}
                        required={hrReviewData.promotion_recommended}
                      >
                        <MenuItem value="">
                          <em>Select Designation</em>
                        </MenuItem>
                        {designations.map((designation) => (
                          <MenuItem key={designation.id} value={designation.id}>
                            {designation.designation_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* HR Comments */}
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    required
                    label="HR Comments & Feedback *"
                    multiline
                    rows={4}
                    value={hrReviewData.hr_overall_comments}
                    onChange={(e) => {
                      setHrReviewData({ ...hrReviewData, hr_overall_comments: e.target.value });
                      // Clear error when user starts typing
                      if (showCommentsError && e.target.value.trim() !== "") {
                        setShowCommentsError(false);
                      }
                    }}
                    placeholder="Provide final assessment, increment justification, and any recommendations..."
                    error={showCommentsError && (!hrReviewData.hr_overall_comments || hrReviewData.hr_overall_comments.trim() === "")}
                    helperText={showCommentsError && (!hrReviewData.hr_overall_comments || hrReviewData.hr_overall_comments.trim() === "") ? "HR comments are required" : ""}
                  />
                </Box>

                {/* Summary Card */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: "white", borderRadius: 1, border: "2px solid", borderColor: "grey.700" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 2 }}>
                    📋 Decision Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Final Rating</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {Number(hrReviewData.hr_overall_rating).toFixed(2)}/5.00
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Grade</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {hrReviewData.final_grade}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Increment</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {hrReviewData.increment_percentage}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Promotion</Typography>
                      <Typography variant="body1" sx={{ fontWeight: "600" }}>
                        {hrReviewData.promotion_recommended ? "Yes ✓" : "No"}
                      </Typography>
                      {hrReviewData.promotion_recommended && hrReviewData.promoted_to_designation_id && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          To: {designations.find(d => d.id === hrReviewData.promoted_to_designation_id)?.designation_name || "N/A"}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={submittingReview || savingDraft}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={submittingReview || savingDraft}
            startIcon={savingDraft ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {savingDraft ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitHRReview}
            disabled={submittingReview || savingDraft || hrReviewData.hr_overall_rating === 0}
            startIcon={submittingReview ? <CircularProgress size={20} /> : null}
          >
            {submittingReview ? "Submitting..." : "Submit HR Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRReview;

